/**
 * `/homework` (#376, #437, D30): put one homework PDF into DaVinci.
 *
 * After an upload the learner is taken to `/homework/:uploadId`; if they leave
 * it, this screen leads back to their newest upload while it is unfinished (#575)
 * - a link, never a redirect, since the learner may want to upload a new sheet
 * instead. Below the form, **Past homework** (#583) lists the latest uploads,
 * newest first, each reopenable and, once checked, with its feedback; the server
 * returns at most 20, and the screen says so when it got that many.
 *
 * What this screen says and does not say:
 *
 * - **The limits are stated before the attempt**, and again when one is hit,
 *   with the sentence "nothing was stored" - a refused upload leaves nothing
 *   behind, and the learner should not wonder.
 * - **The exercise is chosen, never guessed** (#437): nothing is preselected.
 * - **One idempotency key per chosen file and exercise**, minted when either is
 *   chosen and held in a ref (the `today-page.tsx` precedent). A second click,
 *   or a retry after a dropped connection, replays the server's answer instead
 *   of starting a second reading job; the exercise is part of the request path,
 *   so changing it mints a new key.
 * - **"This week" is words only**: a sheet's title, due date and state. No
 *   count, score, percentage or bar.
 * - **Nothing is judged and nothing is stored in the browser.** The file's
 *   bytes go to the server and nowhere else.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { ApiRequestError } from '../../api/client.js';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { useCourseFocus } from '../../app/course-focus.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { usageMessages, useModelLocked } from '../usage/usage-meter.js';
import { useMessages } from '../../i18n/locale.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { morphName } from '../../app/page-morph.js';

/** The server's cap on `GET /homework` (#575). */
const HISTORY_CAP = 20;

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
/** The select's value for one exercise; course refs are digits, so the separator cannot occur in either part. */
const exerciseValue = (courseRef: string, exerciseId: string): string => `${courseRef}/${exerciseId}`;

function looksLikePdf(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

export function HomeworkUploadPage(): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const locked = useModelLocked();
  const usage = useMessages(usageMessages);
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [notPdf, setNotPdf] = useState(false);
  const [missing, setMissing] = useState(false);
  const [exercise, setExercise] = useState('');
  const [exerciseMissing, setExerciseMissing] = useState(false);
  const key = useRef<string | undefined>(undefined);
  const summaryRef = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const exercises = useQuery({
    queryKey: queryKeys.homeworkExercises,
    queryFn: () => api.homeworkExercises(),
  });

  const recent = useQuery({
    queryKey: queryKeys.homeworkList,
    queryFn: () => api.listHomework(),
  });
  // Unfinished: still being read, or read and waiting for the learner's check. A failed read or a checked upload is not.
  const newest = recent.data?.uploads[0];
  const unfinished = newest !== undefined && !newest.checked && newest.state !== 'failed' ? newest : undefined;

  const upload = useMutation({
    mutationFn: async (chosen: { file: File; courseRef: string; exerciseId: string }) => {
      key.current ??= crypto.randomUUID();
      const bytes = await chosen.file.arrayBuffer();
      return api.createHomeworkForExercise(chosen.courseRef, chosen.exerciseId, key.current, bytes);
    },
    onSuccess: (created) => {
      void navigate(`/homework/${encodeURIComponent(created.uploadId)}`);
    },
  });

  const sentence =
    upload.error instanceof ApiRequestError ? (t.uploadFailureSentence(upload.error.code) || undefined) : undefined;
  const consentNeeded =
    upload.error instanceof ApiRequestError && upload.error.code === 'consent_required';
  // Anything the screen has no sentence for goes to the shared notice.
  const unknownFailure = upload.isError && sentence === undefined && !consentNeeded;
  const showSummary =
    notPdf || missing || exerciseMissing || sentence !== undefined || consentNeeded;

  useEffect(() => {
    // Only after a submission the person just made - never on first render.
    if (showSummary) summaryRef.current?.focus();
  }, [showSummary, upload.submittedAt]);

  const { focused } = useCourseFocus();
  // Focus decides what is shown (UX 4.3a); with no focus (no selected course) everything is shown.
  const list = (exercises.data?.exercises ?? []).filter(
    (item) => focused === null || item.courseRef === focused.moodleCourseId,
  );
  const now = Date.now();
  const thisWeek = list.filter((item) => Math.abs(new Date(item.dueAt).getTime() - now) <= WEEK_MS);
  const courses = [...new Set(list.map((item) => item.courseRef))];

  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    choose(event.target.files?.[0] ?? null);
  }

  /** A file dropped on the drop area is chosen exactly as one picked with the file control. */
  function handleDrop(event: React.DragEvent<HTMLLabelElement>): void {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files[0] ?? null;
    if (dropped === null) return;
    if (fileInput.current !== null) fileInput.current.files = event.dataTransfer.files;
    choose(dropped);
  }

  function choose(chosen: File | null): void {
    key.current = undefined;
    upload.reset();
    setMissing(false);
    if (chosen !== null && !looksLikePdf(chosen)) {
      setFile(null);
      setNotPdf(true);
      return;
    }
    setNotPdf(false);
    setFile(chosen);
  }

  function handleExercise(event: React.ChangeEvent<HTMLInputElement>): void {
    key.current = undefined;
    upload.reset();
    setExerciseMissing(false);
    setExercise(event.target.value);
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const picked = list.find((item) => exerciseValue(item.courseRef, item.exerciseId) === exercise);
    // A rejected non-PDF already has its own sentence; do not add a second.
    setMissing(file === null && !notPdf);
    setExerciseMissing(picked === undefined);
    if (file === null || picked === undefined) return;
    upload.mutate({ file, courseRef: picked.courseRef, exerciseId: picked.exerciseId });
  }

  const pickedItem = list.find((item) => exerciseValue(item.courseRef, item.exerciseId) === exercise);
  const titleOf = (courseRef: string | undefined, exerciseId: string | undefined): string | undefined =>
    (exercises.data?.exercises ?? []).find((item) => item.courseRef === courseRef && item.exerciseId === exerciseId)
      ?.title;
  const allUploads = recent.data?.uploads ?? [];
  // Uploads carry their course; with a focused course, only that course's are listed (UX 4.3a).
  const history = allUploads.filter(
    (item) => focused === null || item.courseRef === undefined || item.courseRef === focused.moodleCourseId,
  );
  const unfinishedTitle = titleOf(unfinished?.courseRef, unfinished?.exerciseId);

  return (
    <div className="page homework hw">
      {unfinished !== undefined ? (
        <section className="hw-continue" aria-labelledby="homework-unfinished">
          <span className="hw-continue-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 6v6l4 2" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </span>
          <div className="hw-continue-text">
            <h2 id="homework-unfinished">{t.unfinishedCheckHeading}</h2>
            {unfinishedTitle !== undefined ? (
              <p>
                <bdi dir="auto">{unfinishedTitle}</bdi>
              </p>
            ) : null}
          </div>
          <Link className="hw-continue-link" to={`/homework/${encodeURIComponent(unfinished.uploadId)}`}>
            {t.unfinishedCheckContinue}
          </Link>
        </section>
      ) : null}
      <h1>{t.postHeading}</h1>

      {showSummary ? (
        <div className="notice notice-error" role="alert" tabIndex={-1} ref={summaryRef}>
          <h2>{t.fileNotUploadedHeading}</h2>
          {notPdf ? <p>{t.notPdfChoose}</p> : null}
          {exerciseMissing ? <p>{t.chooseHomeworkMissing}</p> : null}
          {missing ? <p>{t.choosePdfFirst}</p> : null}
          {sentence !== undefined ? <p>{sentence}</p> : null}
          {consentNeeded ? (
            <p>
              {t.consentToKeep} <Link to="/settings/consent">{t.reviewConsent}</Link>.
            </p>
          ) : null}
        </div>
      ) : null}
      {unknownFailure ? <FailureNotice error={upload.error} /> : null}
      {exercises.isError ? (
        <FailureNotice error={exercises.error} onRetry={() => void exercises.refetch()} />
      ) : null}
      {exercises.isSuccess && list.length === 0 ? <p>{t.noHomeworkSetUp}</p> : null}

      <form onSubmit={handleSubmit} className="upload-form">
        <fieldset className="hw-choices" disabled={list.length === 0}>
          <legend className="visually-hidden">{t.chooseHomeworkLabel}</legend>
          {courses.map((courseRef) => (
            <div key={courseRef} role="group" aria-label={t.courseN(courseRef)} className="hw-choice-group">
              {courses.length > 1 ? <p className="hw-course">{t.courseN(courseRef)}</p> : null}
              {(() => {
                const inCourse = list.filter((item) => item.courseRef === courseRef);
                // Sheets still to post come first; everything earlier waits behind a closed disclosure (still selectable).
                const current = inCourse.filter((item) => item.state === 'not_posted' && new Date(item.dueAt).getTime() >= now);
                const earlier = inCourse.filter((item) => !current.includes(item));
                const choice = (item: (typeof list)[number]): React.JSX.Element => {
                  const value = exerciseValue(item.courseRef, item.exerciseId);
                  return (
                    <label key={item.exerciseId} className="hw-choice" style={morphName('sheet', value)}>
                      <input
                        type="radio"
                        name="homework-exercise"
                        value={value}
                        checked={exercise === value}
                        onChange={handleExercise}
                      />
                      <span>
                        <bdi dir="auto">{item.title}</bdi>
                        <small>{t.dueOn(t.dueDate(item.dueAt))}</small>
                      </span>
                    </label>
                  );
                };
                const holdsChosen = earlier.some((item) => exerciseValue(item.courseRef, item.exerciseId) === exercise);
                return (
                  <>
                    {current.map(choice)}
                    {earlier.length > 0 ? (
                      <details className="hw-earlier" open={holdsChosen || current.length === 0 ? true : undefined}>
                        <summary>{t.earlierSheets}</summary>
                        <div className="hw-choice-group">{earlier.map(choice)}</div>
                      </details>
                    ) : null}
                  </>
                );
              })()}
            </div>
          ))}
        </fieldset>
        <label
          htmlFor="homework-file"
          className={`hw-drop${dragging ? ' hw-drop-over' : ''}${file !== null ? ' hw-drop-filled' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <span className="hw-drop-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 16V4" />
              <path d="M6 10l6-6 6 6" />
              <path d="M4 20h16" />
            </svg>
          </span>
          <span className="hw-drop-title">{file !== null ? <bdi dir="auto">{file.name}</bdi> : t.dropZone}</span>
          <span className="hw-drop-limit" id="homework-file-limit">{t.uploadLimit}</span>
          <input
            id="homework-file"
            aria-label={t.homeworkPdfLabel}
            aria-describedby="homework-file-limit"
            ref={fileInput}
            className="hw-drop-input"
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleChange}
          />
        </label>
        <button
          type="submit"
          className="hw-primary"
          disabled={upload.isPending || locked || list.length === 0}
        >
          {upload.isPending ? (
            t.uploading
          ) : pickedItem !== undefined ? (
            <>
              {t.postPrefix}
              <bdi dir="auto">{pickedItem.title}</bdi>
            </>
          ) : (
            t.postGeneric
          )}
        </button>
        {locked ? <p className="field-hint">{usage.modelLockReason}</p> : null}
      </form>

      <section className="hw-week" aria-labelledby="homework-this-week">
        <h2 id="homework-this-week">{t.thisWeekHeading}</h2>
        {exercises.isSuccess && thisWeek.length === 0 ? <p>{t.nothingDue}</p> : null}
        {thisWeek.length > 0 ? (
          <ul className="plain-list hw-week-list">
            {thisWeek.map((item) => (
              <li key={exerciseValue(item.courseRef, item.exerciseId)}>
                <bdi dir="auto">{item.title}</bdi> — {t.dueOn(t.dueDate(item.dueAt))} —{' '}
                {t.exerciseState(item.state, item.solutionOpen)}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section aria-labelledby="homework-history">
        <h2 id="homework-history">{t.historyHeading}</h2>
        {recent.isSuccess && history.length === 0 ? <p>{t.historyEmpty}</p> : null}
        {history.length > 0 ? (
          <ul className="plain-list hw-history">
            {history.map((item) => {
              const title = titleOf(item.courseRef, item.exerciseId);
              const stateClass = item.checked ? 'done' : item.state === 'failed' ? 'failed' : 'open';
              return (
                <li key={item.uploadId} className="hw-history-row">
                  <div style={morphName('upload', item.uploadId)}>
                    <strong>{title !== undefined ? <bdi dir="auto">{title}</bdi> : t.historySheetFallback}</strong>
                    <small>{t.historyPosted(t.dueDate(item.createdAt))}</small>
                  </div>
                  <span className={`hw-pill hw-pill-${stateClass}`}>{t.historyState(item.state, item.checked)}</span>
                  <Link className={item.checked ? 'hw-row-link' : 'hw-row-link hw-row-link-main'} to={`/homework/${encodeURIComponent(item.uploadId)}`}>
                    {t.historyOpen}
                  </Link>
                  {item.checked ? (
                    <Link className="hw-row-link hw-row-link-main" to={`/homework/${encodeURIComponent(item.uploadId)}/feedback`}>
                      {t.historyFeedback}
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        {allUploads.length >= HISTORY_CAP ? <p className="field-hint">{t.historyCap}</p> : null}
      </section>
    </div>
  );
}
